import {
  LDContext,
  useFlags,
  useLDClient,
} from "launchdarkly-react-client-sdk";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Context,
  ContextKey,
  INITIAL_CONTEXT,
} from "~/integrations/launch-darkly";
import {
  LaunchDarklyFeatureFlags,
  parseFeatureFlags,
} from "./feature-flag-utils";
import { createLatestContextIdentifier } from "./latest-context-identifier";

export const useLaunchDarkly = () => {
  const client = useLDClient();
  const flags = useFlags<LaunchDarklyFeatureFlags>();

  const [contexts, setContexts] = useState<LDContext>(INITIAL_CONTEXT);
  const [holdsStaleFlags, setHoldsStaleFlags] = useState(false);
  const identifier = useMemo(
    () => client && createLatestContextIdentifier(client, setHoldsStaleFlags),
    [client],
  );

  useEffect(() => {
    if (!identifier) {
      return;
    }
    identifier.start();
    return identifier.stop;
  }, [identifier]);

  useEffect(() => {
    identifier?.update(contexts);
  }, [identifier, contexts]);

  const features = parseFeatureFlags(holdsStaleFlags ? {} : flags);
  const setContext = useCallback(
    <K extends ContextKey, C extends Context[K]>(key: K, context: C) => {
      setContexts((current) => ({
        ...current,
        [key]: context,
      }));
    },
    [],
  );

  const clearContext = useCallback(() => {
    setContexts(INITIAL_CONTEXT);
  }, []);

  return {
    features,
    setContext,
    clearContext,
  };
};

export type FeatureFlags = ReturnType<typeof parseFeatureFlags>;
